import type { Placement, Step } from "react-joyride";
import { strings } from "@/I18n";

interface AdminDetails {
  cityId: number | null;
  whiteLabelId: number | null;
}

type RoleType = "all" | "super" | "wl" | "city";

/**
 * Extended step interface with navigation metadata
 * Enables the tour to navigate users through actual pages
 */
export interface TourStep extends Omit<Step, "target"> {
  target: string;
  roles: RoleType[];
  /** Route to navigate to before showing step */
  route?: string;
  /** Wait for this selector to exist before proceeding (defaults to target) */
  waitForSelector?: string;
  /** Max wait time in ms (default 3000) */
  waitTimeout?: number;
}

/**
 * Get tour steps filtered by user role
 * - Super Admin: sees all steps (cityId=null, whiteLabelId=null)
 * - Whitelabel Admin: sees most steps, skips super-admin-only (whiteLabelId set, cityId=null)
 * - City Admin: limited view, skips city selector, analytics, admin menu (both set)
 */
export const getTourSteps = (adminDetails: AdminDetails | null): TourStep[] => {
  const isSuperAdmin =
    adminDetails?.cityId === null && adminDetails?.whiteLabelId === null;
  const isWLAdmin =
    adminDetails?.cityId === null && adminDetails?.whiteLabelId !== null;
  const isCityAdmin = adminDetails?.cityId !== null;

  const allSteps: TourStep[] = [
    // Step 0: Welcome
    {
      target: "body",
      placement: "center" as Placement,
      title: strings("tour.welcome.title"),
      content: strings("tour.welcome.content"),
      disableBeacon: true,
      roles: ["all"],
    },
    // Step 1: City Selector (dashboard)
    {
      target: '[data-tour="city-selector"]',
      title: strings("tour.citySelection.title"),
      content: strings("tour.citySelection.content"),
      placement: "bottom" as Placement,
      disableBeacon: true,
      roles: ["super", "wl"],
      route: "/v2/dashboard",
    },
    // Step 2: Map in sidebar (dashboard)
    {
      target: '[data-tour="sidebar-map"]',
      title: strings("tour.fleetMap.title"),
      content: strings("tour.fleetMap.content"),
      placement: "right" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard",
    },
    // Step 3: Navigate to Bikes page
    {
      target: '[data-tour="page-title"]',
      title: strings("tour.vehicleManagement.title"),
      content: strings("tour.vehicleManagement.content"),
      placement: "bottom" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard/bikes",
      waitForSelector: '[data-tour="page-title"]',
      waitTimeout: 5000,
    },
    // Step 4: Search on Bikes page
    {
      target: '[data-tour="search-input"]',
      title: strings("tour.quickSearch.title"),
      content: strings("tour.quickSearch.content"),
      placement: "bottom" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard/bikes",
    },
    // Step 5: Filter on Bikes page
    {
      target: '[data-tour="filter-button"]',
      title: strings("tour.advancedFilters.title"),
      content: strings("tour.advancedFilters.content"),
      placement: "bottom" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard/bikes",
    },
    // Step 6: Navigate to Users page
    {
      target: '[data-tour="page-title"]',
      title: strings("tour.customerHub.title"),
      content: strings("tour.customerHub.content"),
      placement: "bottom" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard/users",
      waitForSelector: '[data-tour="page-title"]',
      waitTimeout: 5000,
    },
    // Step 7: Search on Users page
    {
      target: '[data-tour="search-input"]',
      title: strings("tour.findCustomers.title"),
      content: strings("tour.findCustomers.content"),
      placement: "bottom" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard/users",
    },
    // Step 8: Filter on Users page
    {
      target: '[data-tour="filter-button"]',
      title: strings("tour.customerFilters.title"),
      content: strings("tour.customerFilters.content"),
      placement: "bottom" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard/users",
    },
    // Step 9: Navigate to Rentals page
    {
      target: '[data-tour="page-title"]',
      title: strings("tour.rentalActivity.title"),
      content: strings("tour.rentalActivity.content"),
      placement: "bottom" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard/rentals",
      waitForSelector: '[data-tour="page-title"]',
      waitTimeout: 5000,
    },
    // Step 10: Filter on Rentals page
    {
      target: '[data-tour="filter-button"]',
      title: strings("tour.rentalFilters.title"),
      content: strings("tour.rentalFilters.content"),
      placement: "bottom" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard/rentals",
    },
    // Step 11: Analytics (super/wl admin only)
    {
      target: '[data-tour="sidebar-analytics"]',
      title: strings("tour.analytics.title"),
      content: strings("tour.analytics.content"),
      placement: "right" as Placement,
      disableBeacon: true,
      roles: ["super", "wl"],
      route: "/v2/dashboard",
    },
    // Step 12: User menu
    {
      target: '[data-tour="user-menu"]',
      title: strings("tour.accountSettings.title"),
      content: strings("tour.accountSettings.content"),
      placement: "bottom-end" as Placement,
      disableBeacon: true,
      roles: ["all"],
      route: "/v2/dashboard",
    },
    // Step 13: Finale
    {
      target: "body",
      placement: "center" as Placement,
      title: strings("tour.complete.title"),
      content: strings("tour.complete.content"),
      disableBeacon: true,
      roles: ["all"],
    },
  ];

  return allSteps.filter((step) => {
    const { roles } = step;
    if (roles.includes("all")) return true;
    if (roles.includes("super") && isSuperAdmin) return true;
    if (roles.includes("wl") && isWLAdmin) return true;
    if (roles.includes("city") && isCityAdmin) return true;
    return false;
  });
};

/**
 * Get step as joyride Step (strips custom properties)
 */
export const toJoyrideStep = (step: TourStep): Step => {
  const { roles, route, waitForSelector, waitTimeout, ...joyrideStep } = step;
  return joyrideStep as Step;
};

/**
 * Convert all tour steps to joyride format
 */
export const toJoyrideSteps = (steps: TourStep[]): Step[] => {
  return steps.map(toJoyrideStep);
};

// Export default steps for backwards compatibility (super admin view)
export const tourSteps = getTourSteps(null);
